import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { FlowStatePresentation } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import { GameModel } from "@nolimitcity/slot-game/bin/model/GameModel";
import { SpinSettingsGroup } from "@nolimitcity/slot-keypad/bin/core/view/betpanel/SpinSettingsGroup";
import { NolimitApplication } from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import { KeypadPlugin } from "@nolimitcity/slot-launcher/bin/plugins/KeypadPlugin";
import { gsap } from "gsap";
import { Container, DisplayObject } from "pixi.js";

import { EventsManager, GameEvents } from "../events/EventsManager";
import { TimelineLabels } from "../types/enums";
import { ColorSet, IColorScheme, log } from "../utils/AbstractConsole";
import { AbstractGameMode } from "./AbstractGameMode";

let gameModel: GameModel<any, any>;
export const setModelForFlowPresentation = (model: GameModel<any, any>) => (gameModel = model);

export class AbstractFlowPresentation implements FlowStatePresentation {
    private presentationFinished: boolean = false;
    protected readonly colorScheme: IColorScheme;
    protected controller: FlowStateController;
    protected skipInitialized: boolean = false;

    protected model: GameModel<any, any>;
    protected keypad: KeypadPlugin;
    protected fastSpinBtn: DisplayObject | undefined;

    protected flowPresentationTimeline: gsap.core.Timeline;

    constructor(protected id: string = `AbstractFlowStatePresentation`) {
        this.model = gameModel;
        this.keypad = SlotGame.keypadPlugin;

        //to be removed before going live. used for debug purposes
        if (id.toLowerCase().includes(`idle`)) {
            this.colorScheme = ColorSet.IDLE;
        } else if (id.toLowerCase().includes(`bet`)) {
            this.colorScheme = ColorSet.BET;
        } else if (id.toLowerCase().includes(`result`)) {
            this.colorScheme = ColorSet.RESULT;
        } else if (id.toLowerCase().includes(`win`)) {
            this.colorScheme = ColorSet.WIN;
        }
        //to be removed before going live. used for debug purposes
        this.handleEvents();
    }

    protected get gameMode(): AbstractGameMode {
        return AbstractGameMode.NORMAL;
    }

    protected handleEvents(): void {}

    protected onRestoreStart(): void {
        log(`${this.id} onRestoreStart()`, this.colorScheme);
        this.onBetStarted();
    }

    protected onBetStarted(): void {}

    protected onDefaultStart(): void {
        log(`${this.id} onDefaultStart()`, this.colorScheme);
        this.onBetStarted();
    }

    protected finishPresentation() {
        this.killTimeline();
        this.finishController();
    }

    protected stop() {}

    protected skip() {
        log(`${this.id} SKIP()`, this.colorScheme);
        if (this.skipInitialized) {
            log(`${this.id} SKIP already started!`, this.colorScheme);

            return;
        }

        this.skipInitialized = true;
        log(`${this.id} SKIP started`, this.colorScheme);
    }

    protected initializeTimeline(onComplete: Function | null = null): void {
        this.killTimeline();
        this.flowPresentationTimeline = gsap.timeline({
            onComplete:
                onComplete !== null ? () => onComplete() : this.finishPresentation.bind(this),
            paused: true,
            smoothChildTiming: true,
            autoRemoveChildren: true,
        });

        this.flowPresentationTimeline.data = { name: this.id + `Timeline` };
        this.flowPresentationTimeline.addLabel(TimelineLabels.START_PRESENTATION, 0);
    }

    protected addSpecialAnimations() {}

    // eslint-disable-next-line no-unused-vars

    protected finishController(): void {
        if (this.presentationFinished) {
            return;
        }
        if (this.controller) {
            log(`${this.id} FINISH()`, this.colorScheme);
            this.controller.finish();
            this.presentationFinished = true;
        }
    }

    protected killTimeline(): void {
        if (this.flowPresentationTimeline !== undefined) {
            this.flowPresentationTimeline.pause();
            this.flowPresentationTimeline.kill();
            this.flowPresentationTimeline.clear();
        }
    }

    public start(flowStateController: FlowStateController) {
        log(`${this.id} START()`, this.colorScheme);

        this.controller = flowStateController;
        this.controller.setSkipCallback(() => this.skip());
        if (this.controller.state === `IDLE`) {
            EventsManager.trigger(GameEvents.UPDATE_GAME_MODE, this.gameMode);
        }

        this.presentationFinished = false;
        this.skipInitialized = false;
        this.initializeTimeline();

        if (!gameModel) {
            log(`gameModel not set!`);
        }

        if (this.fastSpinBtn === undefined) {
            this.fastSpinBtn = (
                NolimitApplication.getLayerByName(`Keypad`)?.getChildByName(
                    `SlotKeypadView`
                ) as Container
            )?.children.filter((child) => child instanceof SpinSettingsGroup)[0];
        }

        const isRestore = gameModel.gameData ? false : gameModel.initData.isRestoreState;

        if (isRestore) {
            this.onRestoreStart();
        } else {
            this.onDefaultStart();
        }
    }

    protected showFastSpinButton(): void {
        if (this.fastSpinBtn !== undefined) {
            this.fastSpinBtn.visible = true;
        }
    }

    protected hideFastSpinButton(): void {
        if (this.fastSpinBtn !== undefined) {
            this.fastSpinBtn.visible = false;
        }
    }
}
